import { get, set, castArray } from 'lodash';
import URI from 'urijs';
import lz from 'lz-string';
import moment from 'moment';
import aes from 'crypto-js/aes';
import enc from 'crypto-js/enc-utf8';
import { windowWrapper, appQueryParameters } from '@clubmed/utils';
import appConfig from 'Config';
import { STATUS_CODE_400 } from 'Utils/status-code/statusCode.constants';
import { WRONG_SALESMAN_ID } from 'Components/widgets/contact-info/constants';
import { CXO_STEP } from 'Views/booking/constants';
import gqlClient from 'Utils/graphql-client/graphqlClient';
import * as pleUtils from 'Utils/ple-utils';
import {
  PROPOSAL_SAVE,
  PROPOSAL_UPDATE,
  PROPOSAL_OPPORTUNITYID_SAVE,
} from 'Utils/request/requestDictionary';
import * as constants from './constants';
const CXO_CRYPTO_PASSWORD = 'clubmed-cxo';
export type Traveler = {
  neolid: string;
  isMainContact: boolean;
  isChild: boolean;
  birthdate: timestamp | null | undefined;
};

function cxoDateToTimestamp(cxoDate: string | null | undefined): timestamp | null | undefined {
  return cxoDate ? moment(cxoDate, constants.CXO_DATE_FORMAT).valueOf() : undefined;
}

function _getQueryParametersFromCurrentUri(): {} | null | undefined {
  const location = windowWrapper.getLocation();
  return location ? URI.parseQuery(new URI(location).query()) : null;
}

function _getQueryParametersAndValuesFromCurrentUri(): string | null | undefined {
  const location = windowWrapper.getLocation();
  return location ? new URI(location).search() : null;
}

function _getCxoUrl(): string {
  return URI.decode(get(_getQueryParametersFromCurrentUri(), appQueryParameters.cxo.cxo_url));
}

function _performPostMessage(
  functionToCall: string,
  params: Array<any>,
): Promise<any> | null | undefined {
  const parent = windowWrapper.get('opener') || windowWrapper.get('parent');

  if (parent) {
    const origin = _getCxoUrl();

    return new Promise((resolve, reject) => {
      try {
        parent.postMessage([functionToCall, ...params], origin);
        resolve(undefined);
      } catch (e) {
        reject(undefined);
      }
    });
  }
}

function _getQueryParam(queryParam: string): string | null | undefined {
  return get(_getQueryParametersFromCurrentUri(), queryParam);
}

/**
 * @deprecated
 * @param {*} appContext
 * @returns
 */
function isSeller(appContext: AppContext): boolean {
  return !!(appContext.isDirectCxo || appContext.isIndirectCxo || appContext.isCmta);
}

function getOpportunityGUID(queryParams: Record<string, any>): string | null | undefined {
  return queryParams[appQueryParameters.cxo.cxo_opportunity_guid];
}

function _parseCxoTraveler(
  cxoTraveler: string,
  primaryContactNeolid: string | null | undefined,
): Traveler {
  // cxo_traveler can be an adult with syntax: 'neolid' or a child: 'neolid,birthdate'
  const travelerInfo = cxoTraveler.split(',');
  return {
    neolid: travelerInfo[0],
    isMainContact: travelerInfo[0] === primaryContactNeolid,
    isChild: !!travelerInfo[1],
    birthdate: cxoDateToTimestamp(travelerInfo[1]),
  };
}

function getCxoTravelers(): Array<Traveler> | null | undefined {
  const cxoTravelers = _getQueryParam(appQueryParameters.cxo.cxo_traveler);

  const primaryContactNeolid = _getQueryParam(appQueryParameters.cxo.cxo_primarycontact);

  if (!cxoTravelers) {
    return null;
  }

  const travelers = castArray(cxoTravelers).map((traveler) =>
    _parseCxoTraveler(traveler, primaryContactNeolid),
  );

  // If there is no main contact in the travelers it defaults to the first adult
  if (!travelers.some((traveler) => traveler.isMainContact)) {
    set(
      travelers.find((traveler) => !traveler.isChild),
      'isMainContact',
      true,
    );
  }

  // The main contact is put first in the array to avoid deletion on later participant change
  travelers.sort((a, b) => {
    if (a.isMainContact) {
      return -1;
    }

    return b.isMainContact ? 1 : 0;
  });
  return travelers;
}

function getNeolids(): Array<string> {
  return (getCxoTravelers() || []).map((traveler) => traveler.neolid);
}

function saveCustomerProposal(
  proposalId: string,
  appContext: AppContext,
  customerId: string,
): Promise<any> {
  if (!customerId) {
    return Promise.reject();
  }

  const { cmsContent, cmsRelease } = appContext;
  return gqlClient.runNoCacheQuery(
    `
    mutation {
      saveCustomerProposals(
        customerId : "${customerId}",
        proposalId: "${proposalId}") {
          errorDescription (content:"${cmsContent || ''}" release:"${cmsRelease || ''}")
          errorKey
      }
    }`,
    appContext,
    {
      requestName: PROPOSAL_SAVE,
    },
  );
}

function patchProposal(
  proposalId: string,
  appContext: AppContext,
  operation: string,
  parameters: string,
): Promise<any> {
  return gqlClient.runNoCacheQuery(
    `
    mutation {
      patchProposal(
        id: "${proposalId}",
        operation: "${operation}",
        parameters: "${parameters}") {
          id
      }
    }`,
    appContext,
    {
      requestName: PROPOSAL_UPDATE,
    },
  );
}

function saveCustomerProposalOpportunityId(
  proposalId: string,
  appContext: AppContext,
  customerId: string,
  opportunityId: string,
): Promise<any> {
  if (!customerId) {
    return Promise.reject();
  }

  const { cmsContent, cmsRelease } = appContext;
  return gqlClient.runNoCacheQuery(
    `
    mutation {
      saveCustomerProposals(
        customerId : "${customerId}",
        proposalId : "${proposalId}",
        opportunityId: "${opportunityId}") {
          errorDescription (content:"${cmsContent || ''}" release:"${cmsRelease || ''}")
          errorKey
      }
    }`,
    appContext,
    {
      requestName: PROPOSAL_OPPORTUNITYID_SAVE,
    },
  );
}

const getWebNaBaseURL = () => {
  return `${appConfig.get('na.url')}/CxOInstant.jsp`;
};

const getQueryStringNeolIds = (proposal) =>
  proposal.attendees.list.reduce(
    (acc, { customerId }) => acc + '&cxo_neolid=' + (customerId || ''),
    '',
  );

function redirectNewTabToNA({
  newTab,
  step,
  context,
  queryParams,
  opportunityId,
  proposal,
}: {
  newTab: any;
  step: string;
  context: string;
  opportunityId: string | null | undefined;
  appContext: AppContext;
  proposal: any;
  queryParams: Record<string, any>;
}) {
  let actionCode = '';

  if (step === CXO_STEP.CONFIGURE) {
    actionCode = '&action_code=P';
  }

  newTab.location.href = `${getWebNaBaseURL()}?cxo_opportunity_guid=${opportunityId || ''}&cxo_sales_network_id=${queryParams.cxo_sales_network_id || ''}&cxo_step=${step}&cxo_context=${context}${getQueryStringNeolIds(proposal)}&init_na=${queryParams.init_na || ''}${actionCode}`;
}

function sendFacetsToCxO(facets: Array<any>) {
  _performPostMessage(constants.CXO_FACETS_FUNC_TO_CALL, facets);
}

function getCurrentQueryParamsAndState(
  proposalId: string | null | undefined,
  step: string | null | undefined,
): {} {
  const currentQueryParams = URI.parseQuery(_getQueryParametersAndValuesFromCurrentUri());

  if (proposalId && step) {
    const state = lz.compressToEncodedURIComponent(
      JSON.stringify({
        proposalId,
        step,
      }),
    );
    return { ...currentQueryParams, state };
  }

  return currentQueryParams;
}

function getSigninURIWithRedirection({
  locale,
  proposalId,
  step,
  isCmta,
}: {
  locale: string;
  proposalId: string | null | undefined;
  step: string | null | undefined;
  isCmta: boolean;
}): string {
  const redirectUri = new URI(windowWrapper.getLocation())
    .query(getCurrentQueryParamsAndState(proposalId, step))
    .toString();
  return pleUtils.buildSellerSigninUrl({
    redirectUri,
    locale,
    isCmta,
  });
}

function createCxoProject(
  proposalId: string,
  appContext: AppContext,
  queryParams: Record<string, any>,
  hasCheckedOpt: boolean,
  salesmanID: string | null | undefined,
  step: string,
) {
  const bookingStatus = step === CXO_STEP.PAYMENT ? 'PENDING_PAYMENT' : 'OPTION';
  return gqlClient.runNoCacheQuery(
    `
    mutation {
      bookings(
        proposalId: "${proposalId}"
        opportunityId: "${queryParams.cxo_opportunity_guid || ''}"
        bookingStatus: ${bookingStatus}
        sendingOptionInformation: ${hasCheckedOpt.toString()}
        ${salesmanID ? `salesmanID: "${salesmanID}"` : ''}
      ) {
        bookingId
      }
    }`,
    appContext,
    {
      requestName: PROPOSAL_SAVE,
    },
  );
}

async function saveCustomerProposalAndSendContextToCxO({
  appContext,
  queryParams,
  proposal,
  proposalId,
  salesmanID,
  step,
  shouldSendDocument,
}: {
  appContext: AppContext;
  queryParams: Record<string, any>;
  proposal: Proposal;
  proposalId: string;
  salesmanID?: string;
  step: string;
  shouldSendDocument: boolean;
}): Promise<any> {
  const { bookingId } = queryParams;
  const bookingAlreadyExist = !!bookingId;
  const cxoOpportunityId = getOpportunityGUID(queryParams);
  let cxoProjectId = bookingId || proposalId;
  let newTab;

  if (step !== CXO_STEP.AFFICHAGE) {
    // open a new tab now to not be blocked by web browser
    newTab = window.open();
  }

  try {
    if ((step === CXO_STEP.AFFICHAGE || step === CXO_STEP.PAYMENT) && !bookingAlreadyExist) {
      let cxoProject;

      try {
        cxoProject = await createCxoProject(
          proposalId,
          appContext,
          queryParams,
          shouldSendDocument,
          salesmanID,
          step,
        );
      } catch (error) {
        const status = get(error, 'errors[0].statusCode', STATUS_CODE_400);
        const error_code = get(error, 'errors[0].errorCode', 'BOOKING_UNAVAILABLE');

        if (status === STATUS_CODE_400 && error_code === WRONG_SALESMAN_ID) {
          cxoProject = await createCxoProject(
            proposalId,
            appContext,
            queryParams,
            shouldSendDocument,
            undefined,
            step,
          );
        } else {
          throw error;
        }
      }

      if (cxoProject) {
        cxoProjectId = cxoProject.bookings.bookingId;
      }
    }

    await saveCustomerProposal(
      proposalId,
      appContext,
      get(proposal.attendees.list, '[0].customerId'),
    );

    if (step !== CXO_STEP.AFFICHAGE && newTab) {
      redirectNewTabToNA({
        newTab,
        step,
        appContext,
        queryParams,
        proposal,
        opportunityId: cxoOpportunityId,
        context: cxoProjectId,
      });
    }

    return {
      bookingId: cxoProjectId,
    };
  } catch (err) {
    if (newTab) {
      newTab.close();
    }

    if (pleUtils.hasAuthResponseError(err)) {
      windowWrapper.setLocation(
        getSigninURIWithRedirection({
          locale: appContext.locale,
          proposalId,
          step,
          isCmta: appContext.isCmta,
        }),
      );
    }

    throw err;
  }
}

const handle401GraphqlErrorSeller = (errors: any, appContext: AppContext) => {
  if (get(errors, 'errors.[0].statusCode') === 401) {
    window.location.href = getSigninURIWithRedirection({
      locale: appContext.locale,
      proposalId: null,
      step: null,
      isCmta: appContext.isCmta,
    });
  }
};

const decryptHeptaSeller = (cxoId: string) =>
  aes.decrypt(cxoId.replace(/\s/g, '+'), CXO_CRYPTO_PASSWORD).toString(enc);

const shouldRedirectToBankingForCxo = (appContext: AppContext, queryParams: ParsedUrlQuery) => {
  if (appContext.isDirectCxo || appContext.isIndirectCxo) {
    return Boolean(queryParams?.voxpay === '' || queryParams?.voxpay === null);
  }

  return false;
};

export {
  getOpportunityGUID,
  getCxoTravelers,
  getNeolids,
  isSeller,
  redirectNewTabToNA,
  sendFacetsToCxO,
  patchProposal,
  saveCustomerProposal,
  saveCustomerProposalOpportunityId,
  getCurrentQueryParamsAndState,
  getSigninURIWithRedirection,
  saveCustomerProposalAndSendContextToCxO,
  handle401GraphqlErrorSeller,
  decryptHeptaSeller,
  shouldRedirectToBankingForCxo,
};
